/**
 * Permission codes cho module Sổ Quỹ — FE mirror của BE canonical
 * `banhang-backend/src/api/so-quy/shared/constants/permission-codes.ts` (Story 1.7).
 *
 * ⚠️ ARTIFACT TYPE-SAFETY + DOCUMENTATION — KHÔNG phải enforcement.
 *
 * Enforcement thực tế = **100% external SSO** qua `CompositeAuthGuard`.
 * File này mục đích:
 *   1. Type-safety — `ALL_CHUCNANG_PERMISSION_CODES.WEB_BH_SO_QUY_DUYET` ở TopNav/Router/Button.
 *   2. Sync với BE — nếu BE thêm/bớt code, update file này (story 1.6 thêm `WEB_BH_AUDIT_VIEW`).
 *   3. Register vào `ALL_CHUCNANG_PERMISSION_CODES` (`src/constants/permissionConstants.ts`)
 *      để spread thành 1 object lớn cho `useMenuChucNang().hasChucNangByCode(C.WEB_BH_SO_QUY_*)`.
 *
 * Mirror pattern: `accountChucNangs.ts` (`ACCOUNT_PERMISSION_CODES as const` + READ/MUTATE groups).
 */

/** 14 permission codes cho module Sổ Quỹ. */
export const SO_QUY_PERMISSION_CODES = {
  // Phiếu thu
  WEB_BH_SO_QUY_THU_VIEW: 'WEB_BH_SO_QUY_THU_VIEW',
  WEB_BH_SO_QUY_THU_CREATE: 'WEB_BH_SO_QUY_THU_CREATE',
  WEB_BH_SO_QUY_THU_UPDATE: 'WEB_BH_SO_QUY_THU_UPDATE',
  WEB_BH_SO_QUY_THU_DELETE: 'WEB_BH_SO_QUY_THU_DELETE',
  // Phiếu chi
  WEB_BH_SO_QUY_CHI_VIEW: 'WEB_BH_SO_QUY_CHI_VIEW',
  WEB_BH_SO_QUY_CHI_CREATE: 'WEB_BH_SO_QUY_CHI_CREATE',
  WEB_BH_SO_QUY_CHI_UPDATE: 'WEB_BH_SO_QUY_CHI_UPDATE',
  WEB_BH_SO_QUY_CHI_DELETE: 'WEB_BH_SO_QUY_CHI_DELETE',
  // Duyệt / Hủy (áp dụng cả thu + chi)
  WEB_BH_SO_QUY_DUYET: 'WEB_BH_SO_QUY_DUYET',
  WEB_BH_SO_QUY_HUY: 'WEB_BH_SO_QUY_HUY',
  // Báo cáo
  WEB_BH_SO_QUY_BAO_CAO_VIEW: 'WEB_BH_SO_QUY_BAO_CAO_VIEW',
  WEB_BH_SO_QUY_BAO_CAO_EXPORT: 'WEB_BH_SO_QUY_BAO_CAO_EXPORT',
  // Master data tài khoản quỹ (Epic 5)
  WEB_BH_TAI_KHOAN_QUY_MANAGE: 'WEB_BH_TAI_KHOAN_QUY_MANAGE',
  // Master loại thu chi sổ quỹ
  WEB_BH_LOAI_THU_CHI_MANAGE: 'WEB_BH_LOAI_THU_CHI_MANAGE',
} as const

/** Union type của 14 permission code — compile-time safety. */
export type SoQuyPermissionCode = (typeof SO_QUY_PERMISSION_CODES)[keyof typeof SO_QUY_PERMISSION_CODES]

/** Mảng giá trị (14 string) — tiện iterate / validate runtime. */
export const soQuyPermissionCodeValues: readonly SoQuyPermissionCode[] = Object.freeze(
  Object.values(SO_QUY_PERMISSION_CODES),
) as readonly SoQuyPermissionCode[]

/** Nhóm READ — quyền xem (list/detail/audit). */
export const SO_QUY_READ_PERMISSIONS = [
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_THU_VIEW,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_CHI_VIEW,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_BAO_CAO_VIEW,
] as const

/** Nhóm MUTATE — quyền thay đổi (create/update/delete/duyet/huy/export/taiKhoanQuy). */
export const SO_QUY_MUTATE_PERMISSIONS = [
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_THU_CREATE,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_THU_UPDATE,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_THU_DELETE,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_CHI_CREATE,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_CHI_UPDATE,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_CHI_DELETE,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_DUYET,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_HUY,
  SO_QUY_PERMISSION_CODES.WEB_BH_SO_QUY_BAO_CAO_EXPORT,
  SO_QUY_PERMISSION_CODES.WEB_BH_TAI_KHOAN_QUY_MANAGE,
  SO_QUY_PERMISSION_CODES.WEB_BH_LOAI_THU_CHI_MANAGE,
] as const
